import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import deepEqual from 'deep-equal';
import { useFormHandler } from '../hooks';
import { getNewPatientData } from '../members';
import { axios, constants, notify } from '../index';
import { filterEmpty, formatDateForRequest, isEmpty, parseDate, getUrlPrefix } from '../utilities';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import type {
  PlanMemberFormState,
  PatientData,
  Address,
  MemberFormData,
  PatientUpdateData,
  UsePatientSearchParams,
  UseEmployeeFamilyParams,
  Employee,
  Member,
  CreateEmployeeParams,
  FormValues,
  FamilyMemberInput,
  ApiErrorResponse,
} from './types';
import { EmployeeDetailsData } from '../members/types';

export const IdentificationType = {
  SSN: 'ssn',
  ITIN: 'itin',
  Employee: 'employee',
};

export const IdentificationTypeDisplay = {
  [IdentificationType.SSN]: 'SSN',
  [IdentificationType.ITIN]: 'ITIN',
  [IdentificationType.Employee]: 'Employee ID',
};

export const getFullName = (member: MemberFormData) => {
  if (member.middleName) {
    return `${member.firstName} ${member.middleName} ${member.lastName}`;
  }

  return `${member.firstName} ${member.lastName}`;
};

const unformatPhoneNumber = (phone: string) => phone.replaceAll(/\D/g, '');

const isAdultDob = (dob: string) =>
  dayjs(dob).isBefore(dayjs().subtract(18, 'years')) ||
  dayjs(dob).isSame(dayjs().subtract(18, 'years'));

export const useCreateEmployee = (portal: string, corpId?: string | number) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const createEmployeeRecord = (patientId: string, email?: string) => {
    let url = `${getUrlPrefix(portal)}/employee/`;
    if (portal === constants.PORTALS.ADMIN) {
      url = `${url}?corporation_pk=${corpId}`;
    }
    return axios.post(url, {
      patient: patientId,
      coverage_start_date: formatDateForRequest(dayjs()),
      offer_benefits: true,
      ...(email ? { email } : {}),
    });
  };

  const createFamily = (patientId: string, family: FamilyMemberInput[] = []) =>
    axios
      .post('docs/family/bulk/', {
        dependent_on_id: patientId,
        family: family.map(({ relationship: relation, ...member }) => {
          const corp = corpId != null ? String(corpId) : undefined;
          const { patient, check_payment_address: checkAddress } = getNewPatientData(
            member as unknown as Parameters<typeof getNewPatientData>[0]
          );
          return {
            patient: { ...patient, relation, ...(corp ? { corp } : {}) },
            address: checkAddress,
          };
        }),
      })
      .catch((error: ApiErrorResponse) => {
        notify(`${error.response?.data?.member_name}: ${error.response?.data?.errors}`, 'error');
      });

  const getUpdatedPatientData = (formState: MemberFormData, originalPatient: PatientData) => {
    // Can only update phone number and address so only check for these fields
    const patientData: PatientUpdateData = filterEmpty({
      phone_number: originalPatient.phone_number !== formState.phone ? formState.phone : '',
      email:
        (originalPatient.email || '').localeCompare(formState.email || '', undefined, {
          sensitivity: 'accent',
        }) !== 0
          ? formState.email
          : '',
    });

    // Only add address if address has changed
    const formAddress = {
      address1: formState.address1,
      address2: formState.address2,
      city: formState.city,
      state: formState.state,
      zip: formState.zip,
    };
    if (!deepEqual(originalPatient.address, formAddress)) {
      patientData.address = formAddress;
    }

    return patientData;
  };

  const joinList = (items: string[]) => {
    if (items.length === 1) {
      return items[0];
    }
    return `${items.slice(0, -1).join(', ')} and ${items.slice(-1)}`;
  };

  const createEmployee = useMutation(
    async ({ patient, family = [], originalPatient }: CreateEmployeeParams) => {
      let employeeResponse;
      if (originalPatient && originalPatient?.id) {
        // Compute diff first so we can pass the new email to createEmployeeRecord.
        // The employee is created with the new email, then the patient is patched
        // below to match — keeping both records in sync. Passing email directly
        // to employee creation avoids a conflict when the patient's current email
        // is already in use by another employee.
        const updatedPatientData = getUpdatedPatientData(patient, originalPatient);
        employeeResponse = await createEmployeeRecord(originalPatient.id, updatedPatientData.email);
        if (!isEmpty(updatedPatientData)) {
          let url = `${getUrlPrefix(portal)}/patients/${originalPatient.id}/?employee_id=${employeeResponse.data.data.id}`;
          if (portal === constants.PORTALS.ADMIN) {
            url = `${url}&corporation_pk=${corpId}`;
          }
          await axios.patch(url, updatedPatientData);
        }
        if (family.length > 0) {
          await createFamily(originalPatient.id, family);
        }
      } else {
        // At runtime, patient has all required fields validated by form validation
        const newPatientData = getNewPatientData(
          patient as Parameters<typeof getNewPatientData>[0]
        );
        let url = `${getUrlPrefix(portal)}/patients/`;
        if (portal === constants.PORTALS.ADMIN) {
          url = `${url}?corporation_pk=${corpId}`;
        }
        const patientResponse = await axios.post(url, newPatientData);

        const patientId = patientResponse.data.data.id;
        if (family.length > 0) {
          await createFamily(patientId, family);
        }
        employeeResponse = await createEmployeeRecord(patientId, patient.email);
      }
      return employeeResponse.data.data;
    },
    {
      onSuccess: (employee, { patient, family }) => {
        const successMsg = `SUCCESS! The employee ${getFullName(patient)} has been created.`;
        const familySuccessMsg = `SUCCESS! The employee ${getFullName(
          patient
        )} and family members ${joinList(family?.map(getFullName) || [])} have been created.`;
        notify(family && family.length ? familySuccessMsg : successMsg, 'success');
        if (portal === constants.PORTALS.EMPLOYER) {
          navigate(`/employees/${employee.id}`);
        }
        queryClient.invalidateQueries('employees');
      },
      onError: (error: ApiErrorResponse) => {
        if (error?.response?.data?.member_name) {
          notify(`${error.response.data.member_name}: ${error.response.data.errors}`, 'error');
        } else {
          notify(error?.message || 'An error occurred', 'error');
        }
      },
    }
  );

  return {
    createEmployee,
  };
};

interface UseUpdateMemberParams {
  employee: EmployeeDetailsData;
  member: Member;
  portal: string;
  corpId?: string | number;
}

interface UpdateMemberData {
  patientData: PatientUpdateData;
}

export const useUpdateMember = ({ employee, member, portal, corpId }: UseUpdateMemberParams) => {
  const employeePatientId =
    typeof employee?.patient === 'object' ? employee?.patient?.id : employee?.patient;
  const memberPatientId =
    typeof member?.patient === 'object' ? member?.patient?.id : member?.patient;
  const patientId =
    member?.relationship === constants.RELATIONSHIP_PRIMARY ? employeePatientId : memberPatientId;
  const queryClient = useQueryClient();
  const employeeQuery = useQuery(
    ['employees', employee.id, 'patients', patientId],
    () => {
      let url = `${getUrlPrefix(portal)}/patients/${patientId}/?employee_id=${employee.id}`;
      if (portal === constants.PORTALS.ADMIN) {
        url = `${url}&corporation_pk=${corpId}`;
      }
      return axios.get(url).then((response) => response.data);
    },
    {
      enabled: !!patientId,
    }
  );
  const updateMember = useMutation<unknown, unknown, UpdateMemberData>(
    ({ patientData }: UpdateMemberData) => {
      let url = `${getUrlPrefix(portal)}/patients/${patientId}/?employee_id=${employee.id}`;
      if (portal === constants.PORTALS.ADMIN) {
        url = `${url}&corporation_pk=${corpId}`;
      }
      return axios.patch(url, patientData);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['employees']);
      },
    }
  );

  return {
    memberData: employeeQuery.data || {},
    memberLoading: employeeQuery.isFetching,
    updateMember,
  };
};

const initialFamilyFormState = {
  memberTags: [] as string[],
  firstName: '',
  middleName: '',
  dob: null as string | null,
  lastName: '',
  email: '',
  phone: '',
  identificationType: '',
  identificationId: '',
  address1: ' ',
  address2: '',
  city: '',
  state: '',
  zip: '',
  relationship: '',
  gender: constants.GENDER_TYPE_NOT_TO_SAY,
  useSameAddress: true,
  corp: undefined as string | undefined,
};

export const useAddFamilyForm = () => {
  function validateMemberForm(values: FormValues) {
    const requiredFields = [
      'relationship',
      'firstName',
      'lastName',
      'dob',
      'address1',
      'city',
      'state',
      'zip',
      'identificationType',
    ];

    if (values.identificationType !== constants.TAX_ID_TYPE_MEMBER_NONE) {
      requiredFields.push('identificationId');
    }

    if (
      values.relationship === constants.RELATIONSHIP_SPOUSE ||
      (values.relationship === constants.RELATIONSHIP_DEPENDENT &&
        values.dob &&
        isAdultDob(values.dob as string))
    ) {
      requiredFields.push('phone');
    }

    // validate required fields have values
    const errors = requiredFields.reduce((prevErrors, field) => {
      if (field === 'dob' && !dayjs(values.dob).isValid()) {
        return { ...prevErrors, dob: constants.VALIDATION.INVALID_DOB };
      }
      if (
        field === 'phone' &&
        unformatPhoneNumber(values.phone || '').length !== constants.PHONE_LENGTH
      ) {
        return { ...prevErrors, phone: constants.VALIDATION.PHONE };
      }
      if (!values[field]) {
        return {
          ...prevErrors,
          [field]: constants.VALIDATION.REQUIRED,
        };
      }
      return prevErrors;
    }, {});

    return errors;
  }

  return useFormHandler({
    defaultState: initialFamilyFormState,
    validate: validateMemberForm,
  });
};

export const initialPlanMemberFormState: PlanMemberFormState = {
  memberTags: [] as string[],
  firstName: '',
  middleName: '',
  dob: null as string | null | dayjs.Dayjs,
  lastName: '',
  email: '',
  phone: '',
  identificationType: '',
  identificationId: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  gender: constants.GENDER_TYPE_NOT_TO_SAY,
  relationship: undefined,
};

const defaultOptions = { specifyRelationship: false };

export const usePlanMemberForm = (opts = defaultOptions) => {
  const { specifyRelationship } = opts;
  function validateMemberForm(values: FormValues) {
    const requiredFields = [
      'firstName',
      'lastName',
      'dob',
      'address1',
      'city',
      'state',
      'zip',
      'identificationType',
    ];

    if (values.identificationType !== constants.TAX_ID_TYPE_MEMBER_NONE) {
      requiredFields.push('identificationId');
    }

    if (values.relationship === constants.RELATIONSHIP_PRIMARY || !specifyRelationship) {
      requiredFields.push('email');
    }

    if (
      !specifyRelationship ||
      values.relationship === constants.RELATIONSHIP_PRIMARY ||
      values.relationship === constants.RELATIONSHIP_SPOUSE ||
      values.relationship === constants.RELATIONSHIP_DEPENDENT_ADULT
    ) {
      requiredFields.push('phone');
    }

    // validate required fields have values
    const errors = requiredFields.reduce((prevErrors, field) => {
      // validate special cases
      if (field === 'email' && values.email && !values.email.match(constants.REGEX.EMAIL)) {
        return { ...prevErrors, email: constants.VALIDATION.EMAIL };
      }
      if (field === 'dob' && !dayjs(values.dob).isValid()) {
        return { ...prevErrors, dob: constants.VALIDATION.INVALID_DOB };
      }
      if (
        field === 'phone' &&
        unformatPhoneNumber(values.phone ?? '').length !== constants.PHONE_LENGTH
      ) {
        return { ...prevErrors, phone: constants.VALIDATION.PHONE };
      }
      if (!values[field]) {
        return {
          ...prevErrors,
          [field]: constants.VALIDATION.REQUIRED,
        };
      }
      return prevErrors;
    }, {});

    return errors;
  }

  return useFormHandler({
    defaultState: initialPlanMemberFormState,
    validate: validateMemberForm,
  });
};

export const getMemberFormState = (
  memberData: PatientData
): Partial<PlanMemberFormState> & { relationship?: string } => ({
  memberTags: memberData.member_tags,
  relationship: memberData.relationship,
  firstName: memberData.first_name,
  middleName: memberData.middle_name || '',
  dob: parseDate(memberData.dob),
  lastName: memberData.last_name,
  email: memberData.email,
  phone: memberData.phone_number,
  identificationType: memberData.identification_type,
  identificationId: memberData.identification_id,
  address1: memberData.address?.address1,
  address2: memberData.address?.address2,
  city: memberData.address?.city,
  state: memberData.address?.state,
  zip: memberData.address?.zip,
  gender: memberData.gender, // needed for completeness
});

export const useExistingPatient = (
  employeeData: Partial<PlanMemberFormState>,
  hasExistingPatient: boolean,
  portal: string,
  corpId?: string | number
) => {
  const shouldPiiSearch = Boolean(
    !hasExistingPatient &&
    dayjs(employeeData.dob).isValid() &&
    employeeData.identificationType &&
    [IdentificationType.SSN, IdentificationType.ITIN].includes(employeeData.identificationType) &&
    employeeData.identificationId &&
    employeeData.identificationId.match(constants.REGEX.TID)
  );

  let url = `${getUrlPrefix(portal)}/patients/pii-search/`;
  if (portal === constants.PORTALS.ADMIN) {
    url = `${url}?corporation_pk=${corpId}`;
  }

  const piiSearchQuery = useQuery(
    ['employees', 'pii-search', employeeData],
    () =>
      axios
        .post(url, {
          dob: formatDateForRequest(employeeData.dob),
          identification_type: employeeData.identificationType,
          identification_id: employeeData.identificationId,
        })
        .then((response) => response.data.data),
    {
      enabled: shouldPiiSearch,
    }
  );

  return {
    existingPatient: piiSearchQuery.data || {},
  };
};

export const usePatientSearchState = ({
  open,
  formState,
  onFindPatient,
  portal,
  corpId,
}: UsePatientSearchParams) => {
  const [hasFoundPatient, setHasFoundPatient] = useState(false);
  const [identityIsVerified, setIdentityIsVerified] = useState(false);
  const { existingPatient } = useExistingPatient(formState, hasFoundPatient, portal, corpId);

  useEffect(() => {
    if (existingPatient && existingPatient.id && !hasFoundPatient) {
      setHasFoundPatient(true);
      setIdentityIsVerified(false);
      onFindPatient(existingPatient);
    }
  }, [existingPatient]);

  useEffect(() => {
    if (!open) {
      setHasFoundPatient(false);
      setIdentityIsVerified(false);
    }
  }, [open]);

  return {
    existingPatient,
    hasFoundPatient,
    identityIsVerified,
  };
};

export function useEmployeeFamily({ employee, portal, corpId }: UseEmployeeFamilyParams) {
  const queryClient = useQueryClient();
  const employeePatientId =
    typeof employee?.patient === 'object' ? employee?.patient?.id : employee?.patient;

  const { data: familyData = { primary: {}, family: [] }, isFetching } = useQuery(
    ['employees', employee?.id, 'family'],
    () => {
      let url = `${getUrlPrefix(portal)}/family/?patient_id=${employeePatientId}`;
      if (portal === constants.PORTALS.ADMIN) {
        url = `${url}&corporation_pk=${corpId}`;
      }
      return axios.get(url).then((response) => response.data.data);
    },
    {
      enabled: !!employeePatientId,
    }
  );

  const hasSpouse = useMemo(
    () =>
      familyData.family.some(
        ({ relation }: { relation: string }) => relation === constants.RELATIONSHIP_SPOUSE
      ),
    [familyData]
  );

  const addNewFamilyMember = useMutation(
    (patientParams: FamilyMemberInput) => {
      if (!employeePatientId) {
        return Promise.reject(new Error('Employee is required to add a family member.'));
      }
      const { relationship: relation, ...patientRequest } = patientParams;
      // Patient data is validated by form validation before reaching this point
      const patientData = getNewPatientData({
        ...patientRequest,
        ...(corpId && { corp: String(corpId) }),
      } as Parameters<typeof getNewPatientData>[0]);
      let urlPatients = `${getUrlPrefix(portal)}/patients/`;
      if (portal === constants.PORTALS.ADMIN) {
        urlPatients = `${urlPatients}?corporation_pk=${corpId}`;
      }
      return axios
        .post(urlPatients, patientData)
        .then((response) => response.data.data.id)
        .then((newPatient) => {
          let urlFamily = `${getUrlPrefix(portal)}/family/?patient_id=${newPatient}`;
          if (portal === constants.PORTALS.ADMIN) {
            urlFamily = `${urlFamily}&corporation_pk=${corpId}`;
          }
          return axios.post(urlFamily, {
            dependent_on_id: employeePatientId,
            relation,
          });
        });
    },
    {
      onSuccess: () => {
        if (employee?.id) {
          queryClient.invalidateQueries(['employees', employee.id]);
        }
      },
      onError: (error: Error) => {
        notify(error.message, 'error');
      },
    }
  );

  return {
    loading: isFetching,
    family: familyData,
    hasSpouse,
    addFamilyMember: addNewFamilyMember.mutateAsync,
    isAddingFamily: addNewFamilyMember.isLoading,
  };
}

export default {
  usePatientSearchState,
  getMemberFormState,
  useAddFamilyForm,
  initialPlanMemberFormState,
  usePlanMemberForm,
  useEmployeeFamily,
};
